// Hero section initializer - scoped for Shopify integration
// Can be included multiple times on a page without conflicts
(function() {
  'use strict';
  
  // Initialize hero section with unique section ID
  function initHeroSection(sectionId) {
    const hero = document.getElementById(sectionId);
    if (!hero) return;
    
    const heroInner = hero.querySelector('.hero-inner');
    const colorButtons = hero.querySelectorAll('.hero-variant-options button[data-color]');
    const backgroundImages = hero.querySelectorAll('.bg-container .bg');
    const variantOptions = hero.querySelector('.hero-variant-options');
    const heroCta = hero.querySelector('.hero-cta');
    const bottomFloat = hero.querySelector('.hero-cta-bottom-float');
    const viewportContainer = hero.querySelector('.hero-viewport-container');
    
    if (!heroInner || !backgroundImages.length) return;
    
    let isTransitioning = false;
    let transitionTimeout = null;
    let pendingColor = null;
    
    // Initialize: ensure only the active background is visible
    backgroundImages.forEach(bg => {
      if (!bg.classList.contains('bg-active')) {
        bg.style.opacity = '0';
      } else {
        bg.style.opacity = '1';
      }
    });
    
    // Function to handle visibility and positioning of absolutely positioned elements within hero section
    function handleVisibility() {
      const rect = heroInner.getBoundingClientRect();
      const heroTop = rect.top;
      const heroHeight = rect.height;
      const heroBottom = rect.bottom;
      const viewportHeight = window.innerHeight;
      const isInView = heroTop < viewportHeight && heroTop + heroHeight > 0;
      
      // Show/hide CTA based on whether hero section is in viewport
      if (isInView) {
        if (heroCta) {
          heroCta.style.opacity = '1';
          heroCta.style.pointerEvents = 'auto';
        }
      } else {
        if (heroCta) {
          heroCta.style.opacity = '0';
          heroCta.style.pointerEvents = 'none';
        }
      }
      
      // Position variant selector dynamically based on viewport
      const GAP_BELOW_VARIANT = 24;
      let bottomPosition = 24; // default when no variant options
      const variantSelectorHeight = variantOptions ? (variantOptions.offsetHeight || 56) : 0;

      if (variantOptions && isInView) {
        variantOptions.style.opacity = '1';
        variantOptions.style.pointerEvents = 'auto';
        
        // Calculate available space from bottom of viewport to bottom of hero
        const spaceFromViewportBottom = heroBottom - viewportHeight;
        
        // Get CTA bottom position to ensure no overlap
        let ctaBottom = 0;
        if (heroCta) {
          const ctaRect = heroCta.getBoundingClientRect();
          ctaBottom = ctaRect.bottom - heroTop; // Position relative to hero-inner
        }
        
        // Minimum distance from top of hero to variant selector (to avoid overlap with CTA)
        const minDistanceFromTop = ctaBottom + 40; // 40px padding after CTA
        
        // Minimum distance from bottom of viewport
        const minDistanceFromViewportBottom = 24; // Original bottom: 24px
        
        // Calculate bottom position
        // If hero extends below viewport, position relative to viewport bottom
        // Otherwise, use default position from hero bottom
        if (spaceFromViewportBottom > 0) {
          // Hero extends below viewport - position relative to viewport
          bottomPosition = Math.max(minDistanceFromViewportBottom, spaceFromViewportBottom + minDistanceFromViewportBottom);
        } else {
          // Hero is fully or partially above viewport - use default
          bottomPosition = minDistanceFromViewportBottom;
        }
        
        // Ensure variant selector doesn't overlap with CTA
        const positionFromTop = heroHeight - bottomPosition - variantSelectorHeight;
        
        if (positionFromTop < minDistanceFromTop) {
          // Adjust to maintain minimum distance from top
          bottomPosition = heroHeight - minDistanceFromTop - variantSelectorHeight;
          // Ensure it's not negative
          bottomPosition = Math.max(minDistanceFromViewportBottom, bottomPosition);
        }
        
        // Clamp bottom position between 0 and 200px (max 200px from bottom)
        bottomPosition = Math.min(Math.max(0, bottomPosition), 125);
        
        variantOptions.style.bottom = bottomPosition + 'px';
      } else if (variantOptions) {
        variantOptions.style.opacity = '0';
        variantOptions.style.pointerEvents = 'none';
      }

      // Position rating/divider block — only reposition on mobile/tablet; desktop uses CSS positioning
      if (bottomFloat && viewportContainer) {
        if (isInView) {
          bottomFloat.style.opacity = '1';
          bottomFloat.style.pointerEvents = 'auto';
          if (window.innerWidth > 768) {
            // Desktop: let CSS handle positioning, clear any inline overrides
            bottomFloat.style.top = '';
            bottomFloat.style.bottom = '';
          } else {
            // Mobile/tablet: let CSS handle positioning (bottom: 80px), clear inline top
            bottomFloat.style.top = '';
            bottomFloat.style.bottom = '';
          }
        } else {
          bottomFloat.style.opacity = '0';
          bottomFloat.style.pointerEvents = 'none';
        }
      }
    }
    
    // Function to change background with seamless blend transition
    function changeBackground(color) {
      // If already transitioning, allow mid-transition change to latest selection
      if (isTransitioning) {
        pendingColor = color;
        return;
      }
      
      // Find the target background image within this hero section
      const targetBg = Array.from(backgroundImages).find(bg => bg.dataset.color === color);
      const activeBg = hero.querySelector('.bg-active');
      
      if (!targetBg || targetBg === activeBg) {
        return; // Already showing this background
      }
      
      isTransitioning = true;
      pendingColor = null;
      
      // Clear any existing timeout
      if (transitionTimeout) {
        clearTimeout(transitionTimeout);
      }
      
      // Set the incoming image to be on top with blend mode and start at opacity 0
      targetBg.style.opacity = '0';
      targetBg.classList.add('bg-transitioning');
      
      // Use requestAnimationFrame to ensure both transitions start simultaneously
      requestAnimationFrame(() => {
        // Start blend transition: both images visible with blend mode for seamless color transition
        targetBg.style.opacity = '1';
        
        // After blend transition, fade out the old image
        setTimeout(() => {
          activeBg.style.opacity = '0';
        }, 100);
        
        // After transition completes, clean up and switch to normal mode
        transitionTimeout = setTimeout(() => {
          activeBg.classList.remove('bg-active');
          targetBg.classList.remove('bg-transitioning');
          targetBg.classList.add('bg-active');
          
          // Reset styles for next transition
          activeBg.style.opacity = '';
          targetBg.style.opacity = '';
          targetBg.style.mixBlendMode = '';
          
          isTransitioning = false;
          
          // If there's a pending color change, process it now
          if (pendingColor) {
            const pendingColorValue = pendingColor;
            pendingColor = null;
            changeBackground(pendingColorValue);
          }
        }, 1000); // Match CSS transition duration
      });
    }
    
    // Add click handlers to color buttons for this hero section
    colorButtons.forEach(button => {
      button.addEventListener('click', function() {
        const selectedColor = this.dataset.color;
        
        // Update aria-pressed states for buttons in this hero section
        colorButtons.forEach(btn => {
          btn.setAttribute('aria-pressed', btn === this ? 'true' : 'false');
          // Update border style
          if (btn === this) {
            btn.style.border = '2px solid #ffffff';
          } else {
            btn.style.border = '';
          }
        });
        
        // Change background
        changeBackground(selectedColor);
      });
    });
    
    // Preload all background images for smoother transitions
    backgroundImages.forEach(bg => {
      const img = new Image();
      img.src = bg.src;
    });
    
    // Throttled scroll handler for visibility
    let ticking = false;
    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleVisibility();
          ticking = false;
        });
        ticking = true;
      }
    }
    
    // Use a single scroll listener for all hero sections (shared handler)
    if (!window.heroScrollHandler) {
      window.heroScrollHandler = {
        sections: new Set(),
        handler: function() {
          window.heroScrollHandler.sections.forEach(handleFn => handleFn());
        }
      };
      window.addEventListener('scroll', window.heroScrollHandler.handler, { passive: true });
      window.addEventListener('resize', window.heroScrollHandler.handler, { passive: true });
    }
    
    // Register this section's visibility handler
    window.heroScrollHandler.sections.add(handleVisibility);
    
    // Initial visibility check
    handleVisibility();
  }

  function callDOMHandler_ee_variant_hero() {
    const heroSections = document.querySelectorAll('.hero[data-section-id]');
    heroSections.forEach(hero => {
      const sectionId = hero.getAttribute('data-section-id');
      if (sectionId) {
        initHeroSection(sectionId);
      }
    });
  }
  
  // Auto-initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callDOMHandler_ee_variant_hero);
  } else {
    // DOM already loaded
    callDOMHandler_ee_variant_hero();
  }
  
  // Export init function for manual initialization (useful for Shopify)
  window.initHeroSection = initHeroSection;
})();

